import type { ChecklistStatus } from '../../api/bindings/ChecklistStatus.ts';
import { inputClass } from '../AuthCard.tsx';
import type { EditProps, ViewProps } from './types.ts';

const STATUS_GLYPHS: Record<ChecklistStatus, string> = {
  not_started: '○',
  in_progress: '◐',
  done: '●',
};

const STATUS_TEXT: Record<ChecklistStatus, string> = {
  not_started: 'text-gray-700',
  in_progress: 'text-blue-700',
  done: 'text-gray-400 line-through',
};

export function ChecklistView({ content }: ViewProps<'checklist'>) {
  const groups = content.groups.filter((g) => g.items.length > 0);
  if (groups.length === 0) return null;
  return (
    <div className="space-y-4">
      {groups.map((group, gi) => (
        <div key={gi} className="break-inside-avoid">
          <h4 className="mb-1.5 text-xs font-semibold tracking-wide text-gray-500 uppercase">
            {group.theme}
          </h4>
          <ul className="space-y-1">
            {group.items.map((item) => (
              <li key={item.id} className={`flex items-baseline gap-2 text-sm ${STATUS_TEXT[item.status]}`}>
                <span aria-hidden>{STATUS_GLYPHS[item.status]}</span>
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function ChecklistEdit({ content, onChange }: EditProps<'checklist'>) {
  const setGroup = (gi: number, patch: Partial<(typeof content.groups)[number]>) =>
    onChange({
      ...content,
      groups: content.groups.map((g, j) => (j === gi ? { ...g, ...patch } : g)),
    });
  const setItem = (
    gi: number,
    itemId: string,
    patch: Partial<(typeof content.groups)[number]['items'][number]>,
  ) =>
    setGroup(gi, {
      // never regenerate existing ids — they are stable comment anchors
      items: content.groups[gi].items.map((item) =>
        item.id === itemId ? { ...item, ...patch } : item,
      ),
    });

  return (
    <div className="space-y-4">
      {content.groups.map((group, gi) => (
        <div key={gi} className="rounded-lg border border-gray-200 p-3">
          <div className="mb-2 flex gap-2">
            <input
              className={`${inputClass} font-semibold`}
              placeholder="Group heading"
              value={group.theme}
              onChange={(e) => setGroup(gi, { theme: e.target.value })}
            />
            <button
              type="button"
              className="text-sm whitespace-nowrap text-red-500 hover:underline"
              onClick={() =>
                onChange({ ...content, groups: content.groups.filter((_, j) => j !== gi) })
              }
            >
              Remove group
            </button>
          </div>
          <div className="space-y-2">
            {group.items.map((item) => (
              <div key={item.id} className="flex gap-2">
                <select
                  className="rounded-md border border-gray-300 px-2 py-2 text-sm"
                  value={item.status}
                  onChange={(e) =>
                    setItem(gi, item.id, { status: e.target.value as ChecklistStatus })
                  }
                >
                  <option value="not_started">not started</option>
                  <option value="in_progress">in progress</option>
                  <option value="done">done</option>
                </select>
                <input
                  className={inputClass}
                  placeholder="Action item"
                  value={item.text}
                  onChange={(e) => setItem(gi, item.id, { text: e.target.value })}
                />
                <button
                  type="button"
                  className="text-sm whitespace-nowrap text-red-500 hover:underline"
                  onClick={() =>
                    setGroup(gi, { items: group.items.filter((it) => it.id !== item.id) })
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="text-sm text-blue-600 hover:underline"
              onClick={() =>
                setGroup(gi, {
                  items: [
                    ...group.items,
                    { id: crypto.randomUUID(), text: '', status: 'not_started' },
                  ],
                })
              }
            >
              Add item
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="text-sm text-blue-600 hover:underline"
        onClick={() => onChange({ ...content, groups: [...content.groups, { theme: '', items: [] }] })}
      >
        Add group
      </button>
    </div>
  );
}
